import * as React from "react"
import { Button, StyledModalActions, Text } from "gatsby-interface"
import { MdArrowForward } from "react-icons/md"
import { NoGatsbyError } from "@modules/modal"
import { firebaseIntegrationConfig as text } from "@modules/locales/default.js"
import { CdnVendor } from "@modules/graphql/types"
import { useSetCdnConfigMutation } from "@modules/cdn/shared/queries.generated"
import {
  useFirebaseSitesForSiteIdQuery,
  useGoogleAuthAddedSubscription,
} from "@modules/cdn/firebase/queries.generated"
import { commonMessageTags, FormattedMessage } from "@modules/locales"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import LoadingFromExternalWindow from "@modules/ui/components/LoadingFromExternalWindow"
import { FirebaseIntegrationForm } from "./FirebaseIntegrationForm"
import { CdnIntegrationSharedProps } from "@modules/cdn/shared/types"

enum ConfigurationState {
  Unauthorized = `UNAUTHORIZED`,
  Authorizing = `AUTHORIZING`,
  Authorized = `AUTHORIZED`,
}

export type FirebaseIntegrationWizardProps = CdnIntegrationSharedProps

export function FirebaseIntegrationWizard({
  siteId,
  siteName,
  integration,
  onSetCDNConfig,
  onSubmitFailed,
  onCancel,
}: FirebaseIntegrationWizardProps) {
  const [configurationState, setConfigurationState] = React.useState<
    ConfigurationState
  >(
    integration?.connected
      ? ConfigurationState.Authorized
      : ConfigurationState.Unauthorized
  )
  const [isAuthorized, openAuthWindow] = useFirebaseAuth({
    siteId,
    onAuthorized: () => setConfigurationState(ConfigurationState.Authorized),
    onAuthorizing: () => setConfigurationState(ConfigurationState.Authorizing),
  })
  const [createIntegration] = useSetCdnConfigMutation()

  const {
    data,
    loading: loadingProjects,
    error: projectsQueryError,
  } = useFirebaseSitesForSiteIdQuery({
    variables: {
      siteId,
    },
    skip: !integration.connected && !isAuthorized,
    fetchPolicy: `cache-and-network`,
  })

  const projectsResult = data?.firebaseSitesForSiteId
  const isConnected =
    isAuthorized || (integration.connected && !projectsResult?.reauthorize)

  React.useEffect(() => {
    if (!isConnected) {
      setConfigurationState(ConfigurationState.Unauthorized)
    }
  }, [isConnected])

  const projects = projectsResult?.items ?? []
  const projectsError = projectsResult?.error
  const projectsFinalError = projectsQueryError
    ? projectsQueryError.message
    : projectsError

  return (
    <React.Fragment>
      {projectsFinalError && <ErrorAlert>{projectsFinalError}</ErrorAlert>}
      {configurationState === ConfigurationState.Unauthorized && (
        <React.Fragment>
          <Text>
            <FormattedMessage<"siteName", "strong">
              message={text.headers.connectToSite}
              values={{ siteName }}
              tags={commonMessageTags}
            />
          </Text>
          <StyledModalActions>
            <div />
            <Button onClick={openAuthWindow} rightIcon={<MdArrowForward />}>
              {text.actions.connect}
            </Button>
          </StyledModalActions>
        </React.Fragment>
      )}
      {configurationState === ConfigurationState.Authorizing && (
        <LoadingFromExternalWindow
          css={{ minHeight: `auto` }}
          message={text.messages.authorizingFirebase}
          onClick={openAuthWindow}
        />
      )}
      {configurationState === ConfigurationState.Authorized && (
        <FirebaseIntegrationForm
          initialValues={{
            firebaseSiteId: integration?.config?.serviceId ?? ``,
            firebaseAppName: integration?.config?.accountName ?? ``,
          }}
          projects={projects}
          noProjects={projects.length === 0 && !loadingProjects}
          onSubmit={async values => {
            return createIntegration({
              variables: {
                siteId,
                serviceId: values.projectId,
                serviceName: values.projectName,
                accountName: values.appName,
                vendor: CdnVendor.Firebase,
              },
            })
              .then(({ data }) => {
                return onSetCDNConfig({
                  success: data?.setCDNConfig?.success ?? false,
                  errorType: data?.setCDNConfig?.errorType,
                  ErrorComponent: NoGatsbyError,
                })
              })
              .catch(onSubmitFailed)
          }}
          onCancel={onCancel}
        />
      )}
    </React.Fragment>
  )
}

function useFirebaseAuth({
  siteId,
  onAuthorizing,
  onAuthorized,
}: {
  siteId: string
  onAuthorizing: () => void
  onAuthorized: () => void
}) {
  const [googleToken, setGoogleToken] = React.useState<string>(``)

  const openAuthWindow = () => {
    onAuthorizing()
    window.open(
      `${process.env.GATSBY_DASHBOARD_AUTHENTICATION_URL}/oauth/google?siteId=${siteId}&service=firebase`,
      `_blank`,
      `height=600,width=700`
    )
  }

  useGoogleAuthAddedSubscription({
    variables: {
      siteId,
    },
    onSubscriptionData: ({ subscriptionData }) => {
      if (subscriptionData?.data?.googleAuthAdded) {
        onAuthorized()
        setGoogleToken(subscriptionData.data.googleAuthAdded)
      }
    },
  })

  const isAuthorized = Boolean(googleToken)

  return [isAuthorized, openAuthWindow] as const
}
